import type { ReactNode } from 'react';

/**
 * The screen shell, in one place.
 *
 * Thirteen `<main>` elements across ten screens repeated a variant of
 * `mx-auto flex min-h-screen max-w-… flex-col gap-6 p-6`. Seven used `max-w-lg`
 * (512px) and six `max-w-5xl`, and nothing recorded which a new screen should
 * pick — so the split was accidental rather than designed.
 *
 * Centralising it matters more than the duplication itself: the app's desktop
 * width is now a decision made in one file instead of thirteen. `narrow` gets a
 * modest step-up at `lg` — 512px of content on a 1280px viewport left roughly
 * 60% of the screen empty. A genuine desktop layout (multi-column menus,
 * side-by-side panels) is a design task, not a refactor, and is deliberately
 * not attempted here.
 *
 * P1 additions: the root now owns the page background/foreground via semantic
 * tokens (flips with .dark and data-accent), and an optional sticky `topbar`
 * slot renders above the content at full width. `min-h-dvh` because
 * `min-h-screen` (100vh) overstates the viewport on mobile browsers by the
 * height of the collapsing URL bar, which pushed footers off-screen.
 */
export type ScreenWidth = 'narrow' | 'wide';

const WIDTH: Record<ScreenWidth, string> = {
  // Menus, lists, forms — a single readable column that stops being cramped on
  // a large display.
  narrow: 'max-w-xl lg:max-w-3xl xl:max-w-5xl',
  // Boards, lessons and anything that already had a horizontal layout.
  wide: 'max-w-5xl xl:max-w-7xl 2xl:max-w-[1500px]',
};

export interface ScreenLayoutProps {
  readonly width?: ScreenWidth;
  /** Vertically centre the content — used by loading and empty states. */
  readonly center?: boolean;
  /** Optional sticky app bar (see {@link Topbar}); spans the viewport. */
  readonly topbar?: ReactNode;
  /** Decorative layer behind the whole page (see GameBackdrop); rendered fixed, under the content. */
  readonly backdrop?: ReactNode;
  /** Extra classes appended last, so a screen can still override. */
  readonly className?: string;
  readonly children: ReactNode;
  /** `data-*` attributes (testids, appdom scopes) pass straight through. */
  readonly [dataAttr: `data-${string}`]: unknown;
}

export function ScreenLayout({
  width = 'narrow',
  center = false,
  topbar,
  backdrop,
  className = '',
  children,
  ...rest
}: ScreenLayoutProps) {
  return (
    <div className="relative isolate flex min-h-dvh flex-col text-fg">
      {backdrop}
      {topbar ? (
        <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur">
          <div className="mx-auto w-full max-w-6xl px-4 py-2.5 sm:px-6">{topbar}</div>
        </header>
      ) : null}
      <main
        className={`mx-auto flex w-full flex-1 flex-col gap-6 p-4 sm:p-6 lg:gap-8 lg:p-10 ${WIDTH[width]} ${
          center ? 'items-center justify-center' : ''
        } ${className}`}
        {...rest}
      >
        {children}
      </main>
    </div>
  );
}
